/* eslint-disable */
import React, { useState, useEffect } from "react";
import { message, Popconfirm, Space, Tooltip, Row, Col, Button, Form, Card, Table, Typography } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../../api";
import urls from "../../api/urls";
import Heading from "../../components/Heading";
import { DropDownComponent } from "../Master/CommonComponents";
import { getHubList } from "../../services/api/hub";
import "../Master/Reports/styles.scss";

const { Text } = Typography;
const { GET_TAT_LIST, DELETE_TAT } = urls;

const TATManagement = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hubs, setHubs] = useState([]);
    const [hubLoading, setHubLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    });

    const fetchData = async (page = 1, limit = 10, filterValues = {}) => {
        setLoading(true);
        try {
            // Mapping form field names to API parameters
            const params = {
                page,
                limit,
                source: filterValues.fromHubId,
                destination: filterValues.toHubId,
                mode: filterValues.mode
            };

            // Remove empty params
            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v != null && v !== "")
            );

            const response = await API.Api(GET_TAT_LIST).getwithParams(cleanParams);

            if (response?.status && response?.response?.data) {
                const records = response.response.data.data || [];
                const total = response.response.data.totalCount || records.length;

                setData(records.map((item, index) => ({ ...item, key: item._id || item.id || index })));
                setPagination({
                    ...pagination,
                    current: page,
                    pageSize: limit,
                    total: total,
                });
            } else {
                message.error("Failed to fetch TAT records");
            }
        } catch (error) {
            console.error("Error fetching TAT data:", error);
            message.error("Error fetching TAT data");
        } finally {
            setLoading(false);
        }
    };

    const fetchHubData = async () => {
        try {
            setHubLoading(true);
            const hubData = await getHubList();
            setHubs(hubData);
        } catch (error) {
            console.error("Error fetching hub list:", error);
            message.error("Failed to fetch hub list");
        } finally {
            setHubLoading(false);
        }
    };

    useEffect(() => {
        fetchHubData();
        fetchData(pagination.current, pagination.pageSize);
    }, []);

    const handleTableChange = (paginationInfo) => {
        const filters = form.getFieldsValue();
        fetchData(paginationInfo.current, paginationInfo.pageSize, filters);
    };

    const onFilterChange = () => {
        const filters = form.getFieldsValue();
        fetchData(1, pagination.pageSize, filters);
    };

    const handleDelete = async (id) => {
        try {
            const response = await API.Api(`${DELETE_TAT}${id}`).delete();
            if (response.status) {
                message.success("Record deleted successfully");
                onFilterChange();
            } else {
                message.error("Failed to delete record");
            }
        } catch (error) {
            message.error("Failed to delete record");
        }
    };

    const columns = [
        {
            title: "Source",
            key: "source",
            sorter: false,
            align: "left",
            render: (_, record) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <Text strong>{record.fromHubName}</Text>
                    {record.fromState && (
                        <Text type="secondary" size="small" style={{ fontSize: "11px" }}>
                            <EnvironmentOutlined style={{ fontSize: "10px" }} /> {record.fromState}
                        </Text>
                    )}
                </div>
            )
        },
        {
            title: "Destination",
            key: "destination",
            sorter: false,
            align: "left",
            render: (_, record) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <Text strong>{record.toHubName}</Text>
                    {record.toState && (
                        <Text type="secondary" size="small" style={{ fontSize: "11px" }}>
                            <EnvironmentOutlined style={{ fontSize: "10px" }} /> {record.toState}
                        </Text>
                    )}
                </div>
            )
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            sorter: true,
            align: "center",
        },
        {
            title: "Duration",
            key: "duration",
            align: "center",
            render: (_, record) => `${record.duration} ${record.durationUnit}`,
        },
        {
            title: "Distance",
            key: "distance",
            align: "center",
            render: (_, record) => `${record.distance} ${record.distanceUnit}`,
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Edit">
                        <Button
                            className="master-edit-btn"
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/tatManagement/edit/${record._id || record.id}`)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Are you sure to delete this record?"
                            onConfirm={() => handleDelete(record._id || record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="report-container pt-5">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px", marginBottom: "16px" }}>
                <Heading
                    title="TAT Management"
                    nextPage="/tatManagement/create"
                    nextPageTitle="Add TAT"
                />
            </div>

            <Card className="report-card mb-1">
                <Form form={form} layout="vertical">
                    <Row gutter={[16, 16]} className="filters-section">
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <div className="filter-label">Source HUB</div>
                            <Form.Item name="fromHubId" noStyle>
                                <DropDownComponent
                                    className="small-dropdown"
                                    placeholder="Select Source Hub"
                                    options={hubs}
                                    onChange={onFilterChange}
                                    allowClear
                                    showSearch
                                    loading={hubLoading}
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <div className="filter-label">Destination HUB</div>
                            <Form.Item name="toHubId" noStyle>
                                <DropDownComponent
                                    className="small-dropdown"
                                    placeholder="Select Destination Hub"
                                    options={hubs}
                                    onChange={onFilterChange}
                                    allowClear
                                    showSearch
                                    loading={hubLoading}
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <div className="filter-label">Mode</div>
                            <Form.Item name="mode" noStyle>
                                <DropDownComponent
                                    className="small-dropdown"
                                    placeholder="Filter by Mode"
                                    options={[
                                        { label: "SURFACE", value: "SURFACE" },
                                        { label: "AIR", value: "AIR" },
                                    ]}
                                    onChange={onFilterChange}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <Card className="report-card">
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    rowKey={(record) => record._id || record.id}
                    scroll={{ x: 1000 }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default TATManagement;
